import { EOnboardingStepFriendlyNames } from '~/features/onboarding/models'
import { EClientPath } from '~/models/api'
import {
  EAgentNotifyWhen,
  ELocationTypeValues,
  ENotificationType,
  EPersonalityValues,
  EScheduleValues,
  ETextMethodValues,
} from '~/models/features'

export enum ESettingsStepKeys {
  basicInfo = 'basicInfo',
  creatingLibby = 'creatingLibby',
  tryItOut = 'tryItOut',
  convoOptions = 'convoOptions',
  knowledge = 'knowledge',
  notifications = 'notifications',
  chatbot = 'chatbot',
  phoneText = 'phoneText',
  email = 'email',
  escalations = 'escalations',
}

export enum ESettingsStepFriendlyNames {
  basicInfo = 'Basic Info',
  convoOptions = 'Agent Preferences',
  knowledge = 'Knowledge',
  chatbot = 'Chatbot',
  email = 'Email',
  phoneText = 'Phone/Text',
  escalations = 'Escalations',
  notifications = 'Notifications & More',
}

export const pathByFriendlyStepName = {
  [ESettingsStepFriendlyNames.basicInfo]: EClientPath.settingsBasicInfo,
  [ESettingsStepFriendlyNames.convoOptions]: EClientPath.settingsConvoOptions,
  [ESettingsStepFriendlyNames.knowledge]: EClientPath.settingsKnowledge,
  [ESettingsStepFriendlyNames.chatbot]: EClientPath.settingsChatbot,
  [ESettingsStepFriendlyNames.email]: EClientPath.settingsEmail,
  [ESettingsStepFriendlyNames.phoneText]: EClientPath.settingsPhoneText,
  [ESettingsStepFriendlyNames.notifications]: EClientPath.settingsNotifications,
  [ESettingsStepFriendlyNames.escalations]: EClientPath.settingsEscalations,
}

export enum EBasicInfoKeys {
  businessName = 'businessName',
  service = 'service',
  siteLink = 'siteLink',
  orgId = 'orgId',
  agentTimezone = 'agentTimezone',
}

export enum EKnowledgeV1Keys {
  facts = 'facts',
  siteLink = 'siteLink',
}

export type TBasicInfo = {
  [EBasicInfoKeys.businessName]: string
  [EBasicInfoKeys.service]: string
  [EBasicInfoKeys.siteLink]?: string
  [EBasicInfoKeys.agentTimezone]?: string
}

export type TBasicInfoForm = Omit<TBasicInfo, EBasicInfoKeys.service> & {
  [EBasicInfoKeys.service]: string | null
}

export type TKnowledgeV1InfoForm = {
  [EKnowledgeV1Keys.siteLink]: string
  [EKnowledgeV1Keys.facts]: string[]
}

export enum EConvoOptionsKeys {
  agentName = 'agentName',
  cta = 'cta',
  ctaText = 'ctaText',
  scheduleType = 'scheduleType',
  calendlyLink = 'calendlyLink',
  locationType = 'locationType',
  location = 'location',
  questions = 'questions',
  facts = 'facts', // todo: should be deleted once we launch knowledge feature.
  pricing = 'pricing',
  personality = 'personality',
  customLinks = 'customLinks',
  customInstructions = 'customInstructions',
}

export enum ENotificationsKeys {
  notifyType = 'notifyType',
  notifyMobile = 'notifyMobile',
  mobileConsent = 'mobileConsent',
  notifyEmail = 'notifyEmail',
  notifyWhen = 'notifyWhen',
  zapierId = 'zapierId',
  textMethod = 'textMethod',
}

export enum ENotificationTimingKeys {
  onlyWithQualifiedLeads = 'onlyWithQualifiedLeads',
  all = 'afterAllChats',
}

export enum EEscalationsKeys {
  escalations = 'escalations',
  description = 'description',
  response = 'response',
  phoneResponse = 'phoneResponse',
  phoneTransfer = 'phoneTransfer',
}

export type TNotifications = {
  [ENotificationsKeys.notifyType]: ENotificationType
  [ENotificationsKeys.notifyEmail]: string
  [ENotificationsKeys.notifyMobile]: string
  [ENotificationsKeys.mobileConsent]: boolean
  [ENotificationsKeys.notifyWhen]: EAgentNotifyWhen
  [ENotificationsKeys.zapierId]: string
  [ENotificationsKeys.textMethod]: ETextMethodValues
}

export type TLink = {
  label: string
  url: string
  id: string
}

export enum ECustomInstructionsKeys {
  temperature = 'temperature',
  rulesContext = 'rulesContext',
  customQualifyInstructions = 'customQualifyInstructions',
  customSummaryInstructions = 'customSummaryInstructions',
  customNextstepsInstructions = 'customNextstepsInstructions',
}

export type TCustomInstruction = {
  temperature: number
  rulesContext: string
  customQualifyInstructions: string
  customSummaryInstructions: string
  customNextstepsInstructions: string
}

export type TConvoOptions = {
  [EConvoOptionsKeys.agentName]: string
  [EConvoOptionsKeys.cta]: string
  [EConvoOptionsKeys.ctaText]: string | null
  [EConvoOptionsKeys.scheduleType]: EScheduleValues
  [EConvoOptionsKeys.calendlyLink]: string
  [EConvoOptionsKeys.location]: string
  [EConvoOptionsKeys.locationType]: ELocationTypeValues
  [EConvoOptionsKeys.questions]: string[] | null
  [EConvoOptionsKeys.pricing]: string
  [EConvoOptionsKeys.customLinks]: TLink[]
  [EConvoOptionsKeys.personality]: EPersonalityValues
  [EConvoOptionsKeys.customInstructions]: Array<
    object | TCustomInstruction
  > | null
} & (
  | { [EConvoOptionsKeys.facts]: string[] }
  | { [EConvoOptionsKeys.facts]: never }
)

export type TKnowledge = {
  [EKnowledgeV1Keys.facts]: string[]
  [EKnowledgeV1Keys.siteLink]?: string
}

export enum EPhoneTextKeys {
  phoneStatus = 'phoneStatus',
  phoneVoice = 'phoneVoice',
  phoneIntroRecording = 'phoneIntroRecording',
  phoneNumber = 'phoneNumber',
  phoneIntro = 'phoneIntro',
  agentLanguage = 'agentLanguage',
  textStatus = 'textStatus',
  loopingAudio = 'loopingAudio',
  patience = 'patience',
  phoneRules = 'phoneRules',
  backupNumber = 'backupNumber',
  outageResponse = 'outageResponse',
  textFollowUpClosing = 'textFollowUpClosing',
}

export type TPhoneTextForm = {
  [EPhoneTextKeys.phoneNumber]: string
  [EPhoneTextKeys.phoneStatus]: EPhoneStatus
  [EPhoneTextKeys.agentLanguage]: string
  [EPhoneTextKeys.textStatus]: ETextStatus
  [EPhoneTextKeys.loopingAudio]: ELoopingAudio
  [EPhoneTextKeys.phoneRules]: string
  [EPhoneTextKeys.backupNumber]: string
  [EPhoneTextKeys.outageResponse]: string
  [EPhoneTextKeys.textFollowUpClosing]: string
}

export type TAreaCodeForm = {
  areaCode: string
}

export type TVoiceAndIntroForm = {
  [EPhoneTextKeys.phoneVoice]: string
  [EPhoneTextKeys.phoneIntroRecording]: string
  [EPhoneTextKeys.phoneIntro]: string
  [EPhoneTextKeys.patience]: number
}

export type TVoiceIntroAndTextForm = TVoiceAndIntroForm &
  Omit<TPhoneTextForm, 'phoneNumber'>

export enum EPhoneStatus {
  active = 'active',
  disabled = 'disabled',
}

export enum EOutboundPhoneStatus {
  active = 'active',
  demoMode = 'demoMode',
  paused = 'paused',
  disabled = 'disabled',
}

export enum ETextStatus {
  active = 'active',
  disabled = 'disabled',
  settingUp = 'setting_up',
}

export enum ELoopingAudio {
  off = 'off',
  typing = 'typing',
  office = 'office',
  callCenter = 'callCenter',
  restaurant = 'restaurant',
}

export enum EEmailStatus {
  active = 'active',
  disabled = 'disabled',
}

export enum EAgentLanguage {
  English = 'English',
  Spanish = 'Spanish',
  'Multilingual (10 languages)' = 'Multilingual',
  Bulgarian = 'Bulgarian',
  Cantonese = 'Cantonese',
  Catalan = 'Catalan',
  Czech = 'Czech',
  Danish = 'Danish',
  Dutch = 'Dutch',
  Estonian = 'Estonian',
  Finnish = 'Finnish',
  Flemish = 'Flemish',
  French = 'French',
  German = 'German',
  'German (Switzerland)' = 'German_Switzerland',
  Greek = 'Greek',
  Hindi = 'Hindi',
  Hungarian = 'Hungarian',
  Indonesian = 'Indonesian',
  Italian = 'Italian',
  Japanese = 'Japanese',
  Korean = 'Korean',
  Latvian = 'Latvian',
  Lithuanian = 'Lithuanian',
  Malay = 'Malay',
  'Mandarin (Simplified)' = 'Mandarin_Simplified',
  'Mandarin (Traditional)' = 'Mandarin_Traditional',
  Norwegian = 'Norwegian',
  Polish = 'Polish',
  Portuguese = 'Portuguese',
  Romanian = 'Romanian',
  Russian = 'Russian',
  Slovak = 'Slovak',
  Swedish = 'Swedish',
  Thai = 'Thai',
  Turkish = 'Turkish',
  Ukrainian = 'Ukrainian',
  Vietnamese = 'Vietnamese',
}

export type TEscalations = {
  [EEscalationsKeys.escalations]: string[] | null
  [EEscalationsKeys.description]: string
  [EEscalationsKeys.response]: string
  [EEscalationsKeys.phoneResponse]: string
  [EEscalationsKeys.phoneTransfer]: string
}

export enum EOutboundCallingKeys {
  outboundCallingClientId = 'outboundCallingClientId',
  outboundCallingClientSecret = 'outboundCallingClientSecret',
}

export type TOutboundCalling = {
  [EOutboundCallingKeys.outboundCallingClientId]: string | null
  [EOutboundCallingKeys.outboundCallingClientSecret]: string | null
}

export type TPhoneAndTextEntireForm = TPhoneTextForm &
  TAreaCodeForm &
  TVoiceAndIntroForm
// TODO: remove Partial<{[key in ...]: ...}> after all steps are implemented
export type TSettingsDefinitionSteps = {
  [ESettingsStepFriendlyNames.basicInfo]: Partial<TBasicInfo> | null
  [EOnboardingStepFriendlyNames.creatingLibby]: boolean
  [ESettingsStepFriendlyNames.convoOptions]: Partial<TConvoOptions> | null
  [ESettingsStepFriendlyNames.knowledge]: Partial<TKnowledge> | null
  [ESettingsStepFriendlyNames.notifications]: TNotifications | null
  [ESettingsStepFriendlyNames.chatbot]: {
    layout: TChatbotSettingsLayout
    details: Partial<TChatbotSettingsDetails> | null
  }
  [ESettingsStepFriendlyNames.phoneText]: Partial<TPhoneAndTextEntireForm> | null
  [ESettingsStepFriendlyNames.email]: Partial<TEmail> | null
  [ESettingsStepFriendlyNames.escalations]: TEscalations | null
} & Partial<{
  [key in ESettingsStepFriendlyNames]: unknown
}>

export enum EEmail {
  emailStatus = 'emailStatus',
  agentEmail = 'agentEmail',
  signature = 'signature',
  isDefaultSignature = 'isDefaultSignature',
}
export type TEmail = {
  [EEmail.emailStatus]: EEmailStatus
  [EEmail.agentEmail]?: string
  [EEmail.signature]: string
  [EEmail.isDefaultSignature]: boolean
}

export enum EChatbotDetails {
  chatIntro = 'chatIntro',
  chatbotImgURL = 'chatbotImgURL',
  showLibby = 'showLibby',
  showBranding = 'showBranding',
  chatFriendlyUrl = 'chatFriendlyUrl',
}
export type TChatbotSettingsDetails = {
  [EChatbotDetails.chatIntro]?: string
  [EChatbotDetails.chatbotImgURL]?: string | null
  [EChatbotDetails.showLibby]?: boolean
  [EChatbotDetails.showBranding]?: boolean
  [EChatbotDetails.chatFriendlyUrl]?: string | null
}

export enum EChatbotSelection {
  theme = 'theme',
  linkView = 'Link View',
  simple = 'Simple',
}
export enum EChatbotTheme {
  linkView = 'Link View',
  simple = 'Simple',
}
export type TChatbotSettingsLayout = {
  selection: TChatbotSettingsLayoutSelection
  options: TChatbotSettingsLayoutOptions
}

export type TChatbotSettingsLayoutCustomSelection = {
  [key in EChatbotTheme]: boolean
}

export type TChatbotSettingsLayoutSelection = {
  theme: EChatbotTheme
  custom: TChatbotSettingsLayoutCustomSelection
  [EChatbotTheme.linkView]: EChatbotColorPalette
  [EChatbotTheme.simple]: string
}
export type TChatbotSettingsLayoutOptions = {
  [EChatbotTheme.linkView]: TChatbotLinkViewOptions
  [EChatbotTheme.simple]: string[]
}
export type TChatbotLinkViewOptions = {
  [key in EChatbotColorPalette]: TChatbotColorPalette
}

export enum EChatbotColorPalette {
  teal = 'Teal',
  dark = 'Dark',
  purple = 'Purple',
  neutral = 'Neutral',
}

export type TChatbotColorPalette = {
  mainBackgroundColor: string
  buttonColor: string
  buttonTextColor: string
  accentColor: string
  secondaryTextColor: string
}

export type TColorPalette = {
  [key: string]: TColorPaletteSections
}
export type TColorPaletteSections = {
  mainBackgroundColor: string
  buttonColor: string
  buttonTextColor: string
  accentColor: string
  secondaryTextColor: string
}
export enum EColorPaletteSectionsKeys {
  mainBackgroundColor = 'mainBackgroundColor',
  buttonColor = 'buttonColor',
  buttonTextColor = 'buttonTextColor',
  accentColor = 'accentColor',
  secondaryTextColor = 'secondaryTextColor',
}
export enum EColorPaletteSections {
  mainBackgroundColor = 'Main background color',
  buttonColor = 'Button color',
  buttonTextColor = 'Button text color',
  accentColor = 'Accent color',
  secondaryTextColor = 'Secondary text color',
}

export type TEscalation = {
  description: string
  response: string
  phoneResponse: string
  phoneTransfer: string
  id: string
}

export type TEscalationWithTransferIndex = {
  description: string
  response: string
  phoneResponse: string
  phoneTransfer: string
  phoneTransferIndex: number
  id: string
}
export type TEscalationForm = {
  description: string
  response: string
  phoneResponse: string
  phoneTransfer: string
}

export enum ECredentialTypes {
  outboundCallCredentials = 'outboundCallCredentials',
}

export enum ECredentialKeys {
  clientId = 'clientId',
  clientSecret = 'clientSecret',
}

export type TCredentials = {
  orgId: string
  [ECredentialKeys.clientId]: string
  [ECredentialKeys.clientSecret]: string
  credentialType: ECredentialTypes
}

export type TGetCredentials = {
  clientId: string
  clientSecret: string
  createdDate: string
  credentialType: ECredentialTypes
  id: string
  orgId: string
  updatedDate: string
}

export type TAdditionalVoicesOption = {
  externalId: string
  portalDescription: string
}
